import React, { useState, useEffect } from "react";
import { Eye, Trash2, RotateCcw, User, Landmark, Calendar, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./RegistrationStyles.css";

const url = process.env.REACT_APP_BACKEND_URL;

const RegistrationFeeList = () => {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const navigate = useNavigate();

  const fetchFees = async () => {
    try {
      setLoading(true);
      const endpoint = showInactive
        ? `${url}/api/registrationfee/all`
        : `${url}/api/registrationfee`;

      const res = await axios.get(endpoint);
      setFees(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch registration fees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFees();
  }, [showInactive]);

  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this registration fee?")) {
      try {
        const res = await axios.delete(`${url}/api/registrationfee/${id}`);
        toast.success(res.data.message || "Registration fee deactivated successfully");
        fetchFees();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to deactivate registration fee");
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm("Activate this registration fee?")) {
      try {
        const res = await axios.put(`${url}/api/registrationfee/${id}/restore`);
        toast.success(res.data.message || "Registration fee activated successfully");
        fetchFees();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to activate registration fee");
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) return <div className="loading">Loading registration fees...</div>;

  return (
    <div className="registration-container">
      <div className="header">
        <h2>Registration Fees List</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="toggle-button"
          >
            {showInactive ? 'Show Active Only' : 'Show All'}
          </button>
          <button
            onClick={() => navigate("/registration/new")}
            className="add-button"
          >
            New Registration
          </button>
        </div>
      </div>

      <table className="registration-table">
        <thead>
          <tr>
            <th>Receipt No</th>
            <th>Customer</th>
            <th>Bank</th>
            <th>Account No</th>
            <th>Amount</th>
            <th>Payment Date</th>
            <th>Description</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {fees.length === 0 ? (
            <tr>
              <td colSpan="9" className="no-data">
                No registration fees found
              </td>
            </tr>
          ) : (
            fees.map((fee) => (
              <tr key={fee._id} className={fee.status === 'inactive' ? 'inactive-row' : ''}>
                <td>
                  <div className="customer-info">
                    <DollarSign size={14} />
                    #{fee.no}
                  </div>
                </td>
                <td>
                  <div className="customer-info">
                    <User size={14} />
                    {fee.customer?.fullname || 'N/A'}
                  </div>
                </td>
                <td>{fee.bankname?.bankname || 'N/A'}</td>
                <td>{fee.bankname?.bankaccountno || 'N/A'}</td>
                <td className="fee-amount">{formatCurrency(fee.amount)}</td>
                <td>
                  <div className="customer-info">
                    <Calendar size={14} />
                    {new Date(fee.payment_date).toLocaleDateString()}
                  </div>
                </td>
                <td>{fee.description || '-'}</td>
                <td>
                  <span className={`status-badge ${fee.status}`}>
                    {fee.status === "active" ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="actions">
                  {fee.status === "active" ? (
                    <>
                      <button
                        onClick={() => navigate(`/registration/fee/edit/${fee._id}`)}
                        className="action-btn view"
                        title="Edit"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleSoftDelete(fee._id)}
                        className="action-btn delete"
                        title="Deactivate"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleRestore(fee._id)}
                      className="restore-btn"
                    >
                      <RotateCcw size={14} /> Activate
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RegistrationFeeList;